'use client';

import React, { useState } from 'react';
import {
    TextField,
    Button,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Alert,
    Snackbar,
    Dialog,
    DialogContent,
    IconButton
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { triggerProcessConciergeInquiry } from '../lib/util';
import SmallCustomCalendar from '../components/small_custom_calendar';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../lib/firebase/app';

interface ConciergeQuestionnaireProps {
    open: boolean;
    onClose: () => void;
}

export default function ConciergeQuestionnaire({ open, onClose }: ConciergeQuestionnaireProps) {
    // Form data state
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [dateRequested, setDateRequested] = useState<Date | null>(null);
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [numberOfGuests, setNumberOfGuests] = useState('');
    const [flexibility, setFlexibility] = useState('');
    const [locationPreference, setLocationPreference] = useState('');
    const [venueTypePreference, setVenueTypePreference] = useState('');
    const [message, setMessage] = useState('');

    // UI state
    const [currentStep, setCurrentStep] = useState(1);
    const [sending, setSending] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [consentChecked, setConsentChecked] = useState(true);
    const [showCalendar, setShowCalendar] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [lastDocRefId, setLastDocRefId] = useState<string | null>(null);
    const [processingError, setProcessingError] = useState<string | null>(null);
    const [isRetrying, setIsRetrying] = useState(false);

    const [errors, setErrors] = useState({
        email: '',
        phone: '',
        name: '',
        numberOfGuests: '',
        preferredDate: '',
        preferredTime: '',
        locationPreference: '',
        venueTypePreference: ''
    });

    const validateEmail = (email: string) => {
        if (!email) return true;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validateStep = (step: number) => {
        let isValid = true;
        const newErrors = { ...errors };

        if (step === 1) {
            // Validate date and time
            if (!dateRequested) {
                newErrors.preferredDate = 'Event date is required';
                isValid = false;
            } else {
                newErrors.preferredDate = '';
            }

            if (!startTime || !endTime) {
                newErrors.preferredTime = 'Start and end times are required';
                isValid = false;
            } else {
                newErrors.preferredTime = '';
            }
        } else if (step === 2) {
            // Validate event details
            if (!numberOfGuests) {
                newErrors.numberOfGuests = 'Number of guests is required';
                isValid = false;
            } else {
                newErrors.numberOfGuests = '';
            }
        } else if (step === 3) {
            // Validate contact information
            if (!name.trim()) {
                newErrors.name = 'Name is required';
                isValid = false;
            } else {
                newErrors.name = '';
            }

            if (!email && !phone) {
                newErrors.email = 'Please provide either an email or phone number';
                newErrors.phone = 'Please provide either an email or phone number';
                isValid = false;
            } else {
                newErrors.email = '';
                newErrors.phone = '';
                if (email && !validateEmail(email)) {
                    newErrors.email = 'Please enter a valid email address';
                    isValid = false;
                }
            }
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleContinue = () => {
        if (validateStep(currentStep)) {
            if (currentStep < 3) {
                setCurrentStep(currentStep + 1);
            }
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleSubmit = async () => {
        if (!validateStep(3)) return;
        setSending(true);
        setProcessingError(null);

        try {
            const inquiryData = {
                Name: name,
                Email: email,
                Phone: phone,
                Flexibility: flexibility,
                LocationPreference: locationPreference,
                VenueTypePreference: venueTypePreference,
                Message: message,
                DateRequested: dateRequested ? dateRequested.toISOString().split('T')[0] : '',
                TimeRequested: `${startTime} - ${endTime}`,
                NumberOfGuests: parseInt(numberOfGuests),
                CreatedAt: new Date(),
                Budget: '',
                MustHaves: '',
                Status: 'pending',
                Processed: false,
                Source: 'client'
            };

            // Add directly to Firestore
            const docRef = await addDoc(collection(db, 'concierge_inquiries'), inquiryData);
            setLastDocRefId(docRef.id);

            // If in development, trigger processing immediately
            if (process.env.NODE_ENV === 'development') {
                console.log('Development environment detected - triggering immediate processing');
                try {
                    const result = await triggerProcessConciergeInquiry(docRef.id);
                    console.log('Development processing result:', result);

                    if (!result.success) {
                        setProcessingError(result.message || 'Error processing inquiry');
                    }
                } catch (error) {
                    console.error('Error triggering development processing:', error);
                    setProcessingError('Failed to process inquiry');
                }
            }

            setShowSuccess(true);
            setIsSubmitted(true);
        } catch (error) {
            console.error('Error sending inquiry:', error);
        } finally {
            setSending(false);
        }
    };

    const handleRetryProcessing = async () => {
        if (!lastDocRefId) return;

        setIsRetrying(true);
        setProcessingError(null);

        try {
            console.log('Retrying processing for inquiry:', lastDocRefId);
            const result = await triggerProcessConciergeInquiry(lastDocRefId);
            console.log('Retry processing result:', result);

            if (result.success) {
                setProcessingError(null);
            } else {
                setProcessingError(result.message || 'Error processing inquiry');
            }
        } catch (error) {
            console.error('Error retrying processing:', error);
            setProcessingError('Failed to process inquiry');
        } finally {
            setIsRetrying(false);
        }
    };

    const commonTextFieldStyles = {
        mb: 2,
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            color: '#F0D1FF',
            borderRadius: '12px',
            '& fieldset': {
                borderColor: 'rgba(240, 209, 255, 0.3)',
            },
            '&:hover fieldset': {
                borderColor: '#C0C0DD',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#C0C0DD',
            }
        },
        '& .MuiInputLabel-root': {
            color: '#F0D1FF',
            fontFamily: 'Inter'
        }
    };

    const generateTimeOptions = () => {
        const times = [];
        for (let hour = 6; hour < 30; hour++) {
            const actualHour = hour % 24;
            const period = actualHour >= 12 ? 'PM' : 'AM';
            const displayHour = actualHour === 0 ? 12 : actualHour > 12 ? actualHour - 12 : actualHour;
            const time = `${displayHour}:00 ${period}`;
            times.push(time);
        }
        return times;
    };

    const timeOptions = generateTimeOptions();

    const handleDateSelection = (date: Date) => {
        setDateRequested(date);
        setShowCalendar(false);
    };

    const handleClose = () => {
        if (!sending) {
            onClose();
            // Reset form after a delay to avoid visual glitches during closing animation
            setTimeout(() => {
                if (isSubmitted) {
                    setIsSubmitted(false);
                    setCurrentStep(1);
                    setName('');
                    setEmail('');
                    setPhone('');
                    setDateRequested(null);
                    setStartTime('');
                    setEndTime('');
                    setNumberOfGuests('');
                    setFlexibility('');
                    setLocationPreference('');
                    setVenueTypePreference('');
                    setMessage('');
                    setShowSuccess(false);
                    setConsentChecked(true);
                    setShowCalendar(false);
                }
            }, 300);
        }
    };

    const renderStep1 = () => (
        <Box>
            <Typography
                variant="h4"
                sx={{
                    color: '#F0D1FF',
                    mb: 2,
                    fontFamily: 'Roboto Condensed',
                    fontSize: { xs: '1.5rem', sm: '2.25rem' },
                    fontWeight: 'bold',
                    letterSpacing: '-0.02em',
                    mt: 2
                }}
            >
                When is your event?
            </Typography>

            <Typography
                sx={{
                    color: 'rgba(255, 255, 255, 0.85)',
                    mb: { xs: 3, sm: 4 },
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    lineHeight: 1.6,
                    maxWidth: '650px'
                }}
            >
                Let's start with the date and time for your event.
            </Typography>

            <Box sx={{ mb: 2 }}>
                {!showCalendar && (
                    <>
                        <Button
                            onClick={() => setShowCalendar(true)}
                            sx={{
                                width: '100%',
                                py: 1.5,
                                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                color: '#F0D1FF',
                                borderRadius: '12px',
                                border: !!errors.preferredDate
                                    ? '1px solid #f44336'
                                    : '1px solid rgba(240, 209, 255, 0.3)',
                                textTransform: 'none',
                                justifyContent: 'flex-start',
                                pl: 2,
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                                    borderColor: !!errors.preferredDate ? '#f44336' : '#C0C0DD',
                                },
                                position: 'relative'
                            }}
                        >
                            {dateRequested ? dateRequested.toLocaleDateString() : 'Select Date for Event *'}
                        </Button>
                        {!!errors.preferredDate && (
                            <Typography
                                sx={{
                                    color: '#f44336',
                                    fontSize: '0.75rem',
                                    mt: 0.5,
                                    ml: 1.75
                                }}
                            >
                                {errors.preferredDate}
                            </Typography>
                        )}
                    </>
                )}
                {showCalendar && (
                    <>
                        <div className="hidden md:block">
                            <div style={{ width: '400px', height: '450px', overflow: 'hidden', margin: '0 auto', display: 'flex', justifyContent: 'center' }}>
                                <SmallCustomCalendar
                                    selectedDate={dateRequested || new Date()}
                                    onDateChange={handleDateSelection}
                                    minDate={new Date()}
                                    isHomepage={true}
                                />
                            </div>
                        </div>
                        <div className="block md:hidden">
                            <SmallCustomCalendar
                                selectedDate={dateRequested || new Date()}
                                onDateChange={handleDateSelection}
                                minDate={new Date()}
                                isHomepage={true}
                            />
                        </div>
                    </>
                )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <FormControl fullWidth error={!!errors.preferredTime}>
                    <InputLabel sx={{ color: errors.preferredTime ? '#f44336' : '#F0D1FF' }}>Start Time *</InputLabel>
                    <Select
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        required
                        sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            color: '#F0D1FF',
                            borderColor: errors.preferredTime ? '#f44336' : 'inherit'
                        }}
                    >
                        {timeOptions.map((time) => (
                            <MenuItem key={time} value={time}>{time}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth error={!!errors.preferredTime}>
                    <InputLabel sx={{ color: errors.preferredTime ? '#f44336' : '#F0D1FF' }}>End Time *</InputLabel>
                    <Select
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        required
                        sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            color: '#F0D1FF',
                            borderColor: errors.preferredTime ? '#f44336' : 'inherit'
                        }}
                    >
                        {timeOptions.map((time) => (
                            <MenuItem key={time} value={time}>{time}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
            {!!errors.preferredTime && (
                <Typography
                    sx={{
                        color: '#f44336',
                        fontSize: '0.75rem',
                        mt: -1.5,
                        mb: 2,
                        ml: 1.75
                    }}
                >
                    {errors.preferredTime}
                </Typography>
            )}
        </Box>
    );

    const renderStep2 = () => (
        <Box>
            <Typography
                variant="h4"
                sx={{
                    color: '#F0D1FF',
                    mb: 2,
                    fontFamily: 'Roboto Condensed',
                    fontSize: { xs: '1.5rem', sm: '2.25rem' },
                    fontWeight: 'bold',
                    letterSpacing: '-0.02em',
                    mt: 2
                }}
            >
                Tell us about your event
            </Typography>

            <Typography
                sx={{
                    color: 'rgba(255, 255, 255, 0.85)',
                    mb: { xs: 3, sm: 4 },
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    lineHeight: 1.6,
                    maxWidth: '650px'
                }}
            >
                Help us understand your event requirements and preferences.
            </Typography>

            <TextField
                fullWidth
                label="Number of Guests"
                value={numberOfGuests}
                onChange={(e) => setNumberOfGuests(e.target.value)}
                required
                error={!!errors.numberOfGuests}
                helperText={errors.numberOfGuests}
                sx={commonTextFieldStyles}
            />

            <TextField
                fullWidth
                label="Location Preference"
                value={locationPreference}
                onChange={(e) => setLocationPreference(e.target.value)}
                placeholder="e.g., Manhattan, Brooklyn, specific neighborhood"
                sx={commonTextFieldStyles}
            />

            <TextField
                fullWidth
                label="Venue Type Preference"
                value={venueTypePreference}
                onChange={(e) => setVenueTypePreference(e.target.value)}
                placeholder="e.g., Bar, Restaurant, Rooftop, Raw space"
                sx={commonTextFieldStyles}
            />

            <TextField
                fullWidth
                label="Additional Details"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                multiline
                rows={4}
                placeholder="Tell us about must-haves, nice-to-haves, budget considerations, or any special requirements"
                sx={commonTextFieldStyles}
            />
        </Box>
    );

    const renderStep3 = () => (
        <Box>
            <Typography
                variant="h4"
                sx={{
                    color: '#F0D1FF',
                    mb: 2,
                    fontFamily: 'Roboto Condensed',
                    fontSize: { xs: '1.5rem', sm: '2.25rem' },
                    fontWeight: 'bold',
                    letterSpacing: '-0.02em',
                    mt: 2
                }}
            >
                Last step - your contact info
            </Typography>

            <Typography
                sx={{
                    color: 'rgba(255, 255, 255, 0.85)',
                    mb: { xs: 3, sm: 4 },
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    lineHeight: 1.6,
                    maxWidth: '650px'
                }}
            >
                We'll use this information to send you personalized venue recommendations.
            </Typography>

            <TextField
                fullWidth
                label="Your Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                required
                sx={commonTextFieldStyles}
            />

            <TextField
                fullWidth
                label="Your Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                sx={commonTextFieldStyles}
            />

            <TextField
                fullWidth
                label="Your Phone Number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                error={!!errors.phone}
                helperText={errors.phone}
                sx={commonTextFieldStyles}
            />

            <FormControlLabel
                control={
                    <Checkbox
                        checked={consentChecked}
                        onChange={(e) => setConsentChecked(e.target.checked)}
                        sx={{ color: '#F0D1FF' }}
                    />
                }
                label={
                    <Typography sx={{ color: '#F0D1FF', fontSize: '14px' }}>
                        I agree to receive messages from Litty related to this inquiry
                    </Typography>
                }
            />
        </Box>
    );

    const renderStepIndicator = () => (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, gap: 1 }}>
            {[1, 2, 3].map((step) => (
                <Box
                    key={step}
                    sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: currentStep >= step ? '#C0C0DD' : 'rgba(255, 255, 255, 0.3)',
                        transition: 'background-color 0.3s ease'
                    }}
                />
            ))}
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: '#1C1C34',
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                    overflow: 'hidden',
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogContent sx={{ p: 0, overflow: 'auto' }}>
                <IconButton
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: '#F0D1FF',
                        zIndex: 1
                    }}
                >
                    <CloseIcon />
                </IconButton>

                {isSubmitted ? (
                    <Box
                        sx={{
                            padding: { xs: 4, sm: 6 },
                            backgroundColor: 'transparent',
                            minHeight: '50vh',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            gap: 4
                        }}
                    >
                        <CheckCircleOutlineIcon
                            sx={{
                                fontSize: 90,
                                color: '#C0C0DD',
                                mb: 2
                            }}
                        />
                        <Typography
                            variant="h4"
                            sx={{
                                color: '#F0D1FF',
                                fontFamily: 'Roboto Condensed',
                                fontSize: { xs: '1.75rem', sm: '2.5rem' },
                                fontWeight: 'bold',
                                mb: 2,
                                letterSpacing: '-0.02em'
                            }}
                        >
                            We're on it!
                        </Typography>
                        <Typography
                            sx={{
                                color: 'rgba(255, 255, 255, 0.9)',
                                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                maxWidth: '600px',
                                lineHeight: 1.6,
                                mb: 4
                            }}
                        >
                            Our venue experts are already working on finding the perfect space for your event. We'll be in touch within 24 hours with handpicked recommendations that match your requirements.
                        </Typography>

                        {/* Development mode retry button */}
                        {process.env.NODE_ENV === 'development' && lastDocRefId && (
                            <Box sx={{ mb: 3, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                {processingError && (
                                    <Alert
                                        severity="error"
                                        sx={{
                                            mb: 2,
                                            width: '100%',
                                            maxWidth: '400px',
                                            '& .MuiAlert-message': { color: '#ff4d4d' }
                                        }}
                                    >
                                        {processingError}
                                    </Alert>
                                )}
                                <Button
                                    onClick={handleRetryProcessing}
                                    disabled={isRetrying}
                                    variant="outlined"
                                    startIcon={<RefreshIcon />}
                                    sx={{
                                        py: 1.5,
                                        px: 4,
                                        borderRadius: '9999px',
                                        borderColor: '#C0C0DD',
                                        color: '#C0C0DD',
                                        textTransform: 'none',
                                        fontWeight: 'bold',
                                        fontFamily: 'Inter',
                                        mb: 2,
                                        '&:hover': {
                                            borderColor: '#C0C0DD',
                                            backgroundColor: 'rgba(224, 177, 241, 0.1)',
                                        }
                                    }}
                                >
                                    {isRetrying ? 'Retrying...' : 'Retry Processing (Dev Only)'}
                                </Button>
                                <Typography
                                    sx={{
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        fontSize: '0.85rem',
                                        fontStyle: 'italic'
                                    }}
                                >
                                    Inquiry ID: {lastDocRefId}
                                </Typography>
                            </Box>
                        )}

                        <Button
                            onClick={handleClose}
                            variant="contained"
                            sx={{
                                py: 2,
                                px: 6,
                                fontSize: '1.1rem',
                                borderRadius: '9999px',
                                backgroundColor: '#C0C0DD',
                                color: '#1C1C34',
                                textTransform: 'none',
                                fontWeight: 'bold',
                                fontFamily: 'Inter',
                                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                                '&:hover': {
                                    backgroundColor: '#C0C0DD',
                                    transform: 'scale(1.02)',
                                },
                                '&:active': {
                                    transform: 'scale(0.98)',
                                }
                            }}
                        >
                            Close
                        </Button>
                    </Box>
                ) : (
                    <Box sx={{
                        padding: { xs: 2.5, sm: 4 },
                        backgroundColor: 'transparent',
                    }}>
                        {currentStep === 1 && renderStep1()}
                        {currentStep === 2 && renderStep2()}
                        {currentStep === 3 && renderStep3()}

                        {/* Navigation buttons */}
                        <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'space-between' }}>
                            <Button
                                onClick={handleBack}
                                disabled={currentStep === 1}
                                startIcon={<ArrowBackIcon />}
                                sx={{
                                    py: 1.5,
                                    px: 4,
                                    borderRadius: '9999px',
                                    color: '#C0C0DD',
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    fontFamily: 'Inter',
                                    visibility: currentStep === 1 ? 'hidden' : 'visible',
                                    '&:hover': {
                                        backgroundColor: 'rgba(192, 192, 221, 0.1)',
                                    }
                                }}
                            >
                                Back
                            </Button>

                            <Button
                                onClick={currentStep === 3 ? handleSubmit : handleContinue}
                                disabled={sending || (currentStep === 3 && !consentChecked)}
                                variant="contained"
                                sx={{
                                    py: 1.5,
                                    px: 4,
                                    fontSize: '1rem',
                                    borderRadius: '9999px',
                                    backgroundColor: '#C0C0DD',
                                    color: '#1C1C34',
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    fontFamily: 'Inter',
                                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                                    '&:hover': {
                                        backgroundColor: '#C0C0DD',
                                        transform: 'scale(1.02)',
                                    },
                                    '&:active': {
                                        transform: 'scale(0.98)',
                                    },
                                    '&:disabled': {
                                        backgroundColor: 'rgba(224, 177, 241, 0.5)',
                                        color: 'rgba(44, 44, 84, 0.5)'
                                    }
                                }}
                            >
                                {currentStep === 3
                                    ? (sending ? 'Sending...' : 'Submit')
                                    : 'Continue'
                                }
                            </Button>
                        </Box>
                    </Box>
                )}

                <Snackbar
                    open={showSuccess}
                    autoHideDuration={6000}
                    onClose={() => setShowSuccess(false)}
                >
                    <Alert
                        severity="success"
                        icon={<CheckCircleOutlineIcon />}
                        sx={{ width: '100%' }}
                    >
                        Questionnaire submitted successfully!
                    </Alert>
                </Snackbar>
            </DialogContent>
        </Dialog>
    );
}